// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { CodingArticleUtils } from '@article/util/coding-article.utils';
import { ProjectModel } from '@project/model/project.model';
import { Log } from '@shared/debug/log';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { PropertyUtils } from '@article/util/property-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { CodingFingerArticleManager } from '../manager/coding-finger-article.manager';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { ArticleCreationService } from './article-creation.service';
import { ArticleUtils } from '@article/util/article-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { AlternativeReplaceArticleManager } from '../manager/alternative-replace-article.manager';
import { AddCodingArticleProcessor } from '../processor/add-coding-article.processor';
import { CodingArticleProcessorDelegator } from '../processor/coding-article-processor.delegator';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { IsProjectType } from '../../../model/is-project-type';
import { AttachedArticleContainer } from './article-container-attached.service';

@Injectable({ providedIn: 'root' })
export class AttachCodingFingerArticleService {
    private _articleUsagesToAttachCodingFingers: ArticleUsageVO[] = [];
    private _attachedCodingFingers: ArticleContainer[] = [];
    private _positionsForCodingFingers: string[] = [];
    private _currentArticleUsage: ArticleUsageVO = null;

    constructor(
        private projectModel: ProjectModel,
        private codingArticleUtils: CodingArticleUtils,
        private undoRedoManager: UndoRedoManager,
        private undoRedoUtils: UndoRedoUtils,
        private articleManager: ArticleManager,
        private addCodingArticleProcessor: AddCodingArticleProcessor,
        private codingArticleProcessorDelegator: CodingArticleProcessorDelegator,
        private codingFingerArticleManager: CodingFingerArticleManager,
        private articleCreationService: ArticleCreationService,
        private containerService: ContainerService,
        private alternativeReplaceArticleManager: AlternativeReplaceArticleManager
    ) {
        this.subscribeOnAddedArticles();
    }

    /**
     *  there are 2 types for this task: first ones the coding finger will just be added, second ones are done by raplacing the module.
     *  both tasks are running asynchronously and after each other, UndoRedo will be collected as sequence
     *
     * @param {string[]} articleUsageIds
     * @memberof CodingFingerArticleManager
     */
    public async attachCodingFingersToArticles(articleUsageIds: string[]): Promise<void> {
      const articleUsagesToAttachFingers: ArticleUsageVO[] = [];
      this.codingFingerArticleManager.articleListToReplace = [];
      for (const usageId of articleUsageIds) {
        const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(usageId);
        if (!articleUsage) {
          continue;
        }

        const alternativeArticle = this.codingArticleUtils.getAttachCodingfingerAlternativeArticle(articleUsage);
        if (alternativeArticle) {
          this.codingFingerArticleManager.articleListToReplace.push(articleUsage);
        } else if (CodingArticleUtils.getRemovedCodingFingerArticleId(articleUsage)) {
          articleUsagesToAttachFingers.push(articleUsage);
        }
      }

      this.undoRedoManager.startUndoOperationSequence();

      try {
        await this.startAttachCodingFingersOperation(articleUsagesToAttachFingers)
        await this.startAsynchronAttachCodingFingerByReplace();
      } catch (error) {
        Log.error('Could not start coding finger attachment: ', error)
      }
    }

    private async startAsynchronAttachCodingFingerByReplace(): Promise<void> {
      await this.asynchronAttachCodingFingerByReplace();
    }

    /**
     * This method will be called recursivly by handler
     *
     * @private
     * @memberof CodingFingerArticleManager
     */
    private async asynchronAttachCodingFingerByReplace():Promise<void> {
      if (this.codingFingerArticleManager.articleListToReplace.length > 0 ) {
        const articleUsage = this.codingFingerArticleManager.articleListToReplace.pop();
        await this.codingFingerArticleManager.replaceArticle(this.alternativeReplaceArticleManager, articleUsage, this.codingArticleUtils.getAttachCodingfingerAlternativeArticle(articleUsage));
      } else {
        this.codingFingerArticleManager.finishAttachDetachOperation();
      }
    }

    public async attachCodingFingersToConnections(selectedMetaUsageIdentities: MetaElementUsageIdentityVO[]) {
      const articleUsagesToAttachFingersTo: ArticleUsageVO[] = [];
      const codingFingerPositions: string[] = [];
      // for each (var metaUsageIdentity:SelectionMetaElementUsageIdentity in selectedMetaUsageIdentities)
      for (const metaUsageIdentity of selectedMetaUsageIdentities) {
        const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(metaUsageIdentity.articleUsageId);
        if (!articleUsage) {
          continue;
        }
        const article: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
        if (!article) {
          continue;
        }
        if (CodingArticleUtils.getRemovedCodingFingerArticleId(articleUsage, metaUsageIdentity.metaElementName)) {
          for (const metaElement of article.metaElements) {
            if (metaElement.name === metaUsageIdentity.metaElementName) {
              if (
                (metaElement as ConnectionAreaVO) &&
                (PropertyUtils.getStringPropertyValue((metaElement as ConnectionAreaVO).connectionRequirements, ArticleConnectionPropertyDef.TYPE) ===
                  ArticleConnectionPropertyValue.CODING_FINGER_CONNECTION_TYPE ||
                  PropertyUtils.getStringPropertyValue((metaElement as ConnectionAreaVO).connectionRequirements, ArticleConnectionPropertyDef.TYPE) ===
                  ArticleConnectionPropertyValue.CONTACT_CONNECTION_TYPE)
              ) {
                articleUsagesToAttachFingersTo.push(articleUsage);
                // var position:int = CodingArticleUtils.getPositionFromConnectionName(metaElement.name);
                codingFingerPositions.push(metaElement.name);
              }
            }
          }
        }
      }
      if (ArrayUtils.isNotEmpty(articleUsagesToAttachFingersTo)) {
        await this.startAttachCodingFingersOperation(articleUsagesToAttachFingersTo, codingFingerPositions);
      }
    }

    private async startAttachCodingFingersOperation(articleUsagesToAttachFingers: ArticleUsageVO[], codingFingerPositions: string[] = null): Promise<void> {
      this.codingFingerArticleManager.isActive = true;
      this._articleUsagesToAttachCodingFingers = articleUsagesToAttachFingers;
      this._positionsForCodingFingers = codingFingerPositions;
      await this.attachNextCodingFinger();
    }

    private async attachNextCodingFinger(): Promise<void> {
      // clearCreationManager();
      if (ArrayUtils.isNotEmpty(this._articleUsagesToAttachCodingFingers)) {
        let name: string = '';
        this._currentArticleUsage = this._articleUsagesToAttachCodingFingers.pop();
        if (ArrayUtils.isNotEmpty(this._positionsForCodingFingers)) {
          name = this._positionsForCodingFingers.pop();
        }
        await this.addCodingArticleProcessor.addCodingFinger(this._currentArticleUsage, 1, name, true);
      } else {
        this.finalizeAttachOperation();
      }
    }

    private subscribeOnAddedArticles(): void {
      this.codingFingerArticleManager.addedArticlesSubscription = this.articleCreationService.articlesAdded$.subscribe({
        next: async (data) => {
          if (data.type !== 'articlesAdded') {
            return;
          }
          data.success ? await this.articlesAddedSuccess() : this.articlesAddedFailure();
        },
        error: (error) => Log.error('Error in added articles subscription: ', error)
      })
    }

    private async articlesAddedSuccess(): Promise<void> {
      if (!this.codingFingerArticleManager.isActive) {
        return;
      }
      const createdContainers: ArticleContainer[] = this.codingArticleProcessorDelegator.getCreatedArticleContainers();
      if (ArrayUtils.isNotEmpty(createdContainers)) {
        this._attachedCodingFingers = this._attachedCodingFingers.concat(createdContainers);
      }
      await this.attachNextCodingFinger();
    }

    private articlesAddedFailure(): void {
      if (!this.codingFingerArticleManager.isActive) {
        return;
      }
      this.finalizeAttachOperation();
    }

    private finalizeAttachOperation(): void {
      const attachOperation: UndoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByArticleContainers(this._attachedCodingFingers);
      if (attachOperation) {
        this.undoRedoManager.pushUndo(attachOperation);
      }
      this.clearCreationManager();
      this.clear();
    }

    private clearCreationManager(): void {
      this.codingArticleProcessorDelegator.clean();
    }

    private clear(): void {
      this.codingFingerArticleManager.isActive = false;
      this._attachedCodingFingers = [];
      this._articleUsagesToAttachCodingFingers = [];
      this._currentArticleUsage = null;
    }

    public processAttachedCodingFinger(data: AttachedArticleContainer): void {
      const articleUsageId: string = data && data.container ? data.container.articleGUID : null;
      if (!articleUsageId) {
        return;
      }

      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
      if (!articleContainer) {
        return;
      }
      const codingFingerUsage: ArticleUsageVO = articleContainer.articleUsage;
      const parentArticleUsage: ArticleUsageVO = this.codingFingerArticleManager.getBackwardConnectedArticleUsage(codingFingerUsage);
      if (parentArticleUsage && (CodingArticleUtils.isCodingFinger(articleContainer.article) || ArticleUtils.isContact(articleContainer.article))) {
        this.prepareAttachedCodingFingerUsage(codingFingerUsage, parentArticleUsage, articleContainer);
      }

      if (!this.codingFingerArticleManager.isCodingFingerHandlingEnabled()) {
        return;
      }

      if(this.isArticleMounted(parentArticleUsage)) {
        return;
      }

      if (!CodingArticleUtils.isCodingFinger(articleContainer.article) && !ArticleUtils.isContact(articleContainer.article)) {
        this.codingFingerArticleManager.continueQueueProjectPropertyUpdate();
        return;
      }

      if (parentArticleUsage) {
          this.codingFingerArticleManager.queueProjectPropertyUpdate(parentArticleUsage);
      }
    }

    private isArticleMounted(parentArticleUsage: ArticleUsageVO): boolean {
      // in case this is not mcs/gds check that the article is mounted on a free mounting rail directly

      if (!IsProjectType.mcsGds(this.projectModel.currentProject)) {
        const grandParentArticleUsage: ArticleUsageVO = this.codingFingerArticleManager.getBackwardConnectedArticleUsage(parentArticleUsage);
        if (!grandParentArticleUsage) {
          return true;
        }
        const grandparentArticle: ArticleVO = this.articleManager.getCachedArticle(grandParentArticleUsage.articleId, grandParentArticleUsage.articleVersion);
        if (!ArticleUtils.isFreeMounting(grandparentArticle)) {
          return true;
        }
      }
      return false;
    }

  private prepareAttachedCodingFingerUsage(codingFingerUsage: ArticleUsageVO, parentArticleUsage: ArticleUsageVO, articleContainer: ArticleContainer) {
    const metaNameOnParentArticle: string =
      (codingFingerUsage.metaElementUsages[0] as ConnectionAreaUsageVO).connection.acceptingAreaId.metaElementName;
    codingFingerUsage.groupId = parentArticleUsage.groupId;
    if (!ArticleUsageUtils.isCompositePartArticleUsage(codingFingerUsage)) {
      // transfer composite information only for newly created coding fingers (e.g. that were not created by undo/redo operation)
      const compositePartUsageId: string = CodingArticleUtils.getRemovedCodingFingerCompositePartUsageId(
        parentArticleUsage, metaNameOnParentArticle);
      CompositeArticleUtils.copyCompositeArticleInformationStackFromUsage(parentArticleUsage, codingFingerUsage, compositePartUsageId);
      if (IsProjectType.mcsGds(this.projectModel.currentProject)) {
        CompositeArticleUtils.copyStrippedCompositeArticleInformationFromUsage(parentArticleUsage, codingFingerUsage);
      }
    }
    if (ArticleUtils.isContact(articleContainer.article)) {
      CodingArticleUtils.clearRemovedContactArticleInformation(parentArticleUsage, codingFingerUsage, true);
    } else {
      CodingArticleUtils.clearRemovedCodingFingerArticleInformation(parentArticleUsage, true);
    }
  }
}
